import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, BanknoteIcon, Landmark, CreditCard, DollarSign, CheckSquare } from 'lucide-react';
import Button from '../ui/Button';
import { formatCurrency } from '../../utils/formatters';

const PAYMENT_METHODS = [
  { id: 'cash' as const, label: 'Efectivo', icon: BanknoteIcon },
  { id: 'transfer' as const, label: 'Transferencia', icon: Landmark },
  { id: 'card' as const, label: 'Tarjeta', icon: CreditCard },
  { id: 'mercadopago' as const, label: 'MercadoPago', icon: DollarSign },
  { id: 'check' as const, label: 'Cheque', icon: CheckSquare },
];

interface SalaryPayment {
  id: string;
  employee_name?: string;
  total_amount: number;
  period_start: string;
  period_end: string;
}

interface MarkPaidModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (paymentMethod: string, paymentDate: string) => Promise<void>;
  payment: SalaryPayment | null;
}

const MarkPaidModal: React.FC<MarkPaidModalProps> = ({ isOpen, onClose, onConfirm, payment }) => {
  const [method, setMethod] = useState('transfer');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Fecha por defecto: último día del periodo que se está pagando (ej. sueldos de enero → 31/1)
      const defaultDate = payment?.period_end ? payment.period_end.slice(0, 10) : new Date().toISOString().slice(0, 10);
      setDate(defaultDate);
      setMethod('transfer');
    }
  }, [isOpen, payment?.period_end]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!payment) return;
    setLoading(true);
    try {
      await onConfirm(method, date);
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (!payment) return null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[100] bg-black/50" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[100] w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-xl overflow-hidden animate-fade-in"
          onInteractOutside={(e) => {
            const t = e.target as HTMLElement;
            if (t.closest('[data-radix-select-viewport], [role="listbox"], [role="option"], [data-radix-popper-content-wrapper]')) e.preventDefault();
          }}
        >
          <div className="px-6 pt-6 pb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Marcar como pagado</h2>
              <button type="button" onClick={onClose} className="p-2 -m-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-6 p-4 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 border-2 border-indigo-100 dark:border-indigo-800">
              <p className="text-sm text-indigo-700 dark:text-indigo-300 font-medium">{payment.employee_name}</p>
              <p className="text-2xl font-bold text-indigo-900 dark:text-indigo-100 mt-1">{formatCurrency(payment.total_amount)}</p>
              <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">Se creará un gasto en la categoría Sueldos</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Método de pago</p>
                <div className="flex flex-wrap gap-2">
                  {PAYMENT_METHODS.map((pm) => {
                    const Icon = pm.icon;
                    return (
                      <button
                        key={pm.id}
                        type="button"
                        onClick={() => setMethod(pm.id)}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition-all ${
                          method === pm.id
                            ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                            : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span className="text-sm font-medium">{pm.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Fecha de pago</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full h-12 px-4 rounded-xl border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
                <Button type="submit" disabled={loading} className="flex-1">{loading ? 'Procesando...' : 'Confirmar y crear gasto'}</Button>
              </div>
            </form>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default MarkPaidModal;
